import React, { Component } from 'react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to an error reporting service
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ errorInfo });
    
    // Send to error tracking service if available
    if (window.errorTrackingService) {
      window.errorTrackingService.captureException(error, { extra: errorInfo });
    }
  }

  render() {
    if (this.state.hasError) {
      const { fallback } = this.props;
      
      // Use custom fallback component if provided
      if (fallback) {
        return React.createElement(fallback, {
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          resetError: () => this.setState({ hasError: false, error: null, errorInfo: null })
        });
      }
      
      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-24">
          <div className="max-w-lg w-full bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
            </div>
            <h2 className="mb-4 text-xl font-semibold text-center text-gray-800">Something went wrong</h2>
            <p className="mb-6 text-center text-gray-600">
              An unexpected error has occurred. Our team has been notified.
            </p>
            <div className="mb-4 p-4 bg-gray-100 rounded overflow-auto max-h-48 text-sm font-mono">
              <details>
                <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                  Technical Details
                </summary>
                <p className="mt-2 text-gray-800">
                  {this.state.error?.toString()}
                </p>
                {this.state.errorInfo?.componentStack && (
                  <pre className="mt-2 text-xs text-gray-700 whitespace-pre-wrap">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </details>
            </div>
            <div className="text-center">
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;